import React, { useMemo } from 'react';
import { X, Shield, ArrowUpRight } from 'lucide-react';
import { Button } from '../UI/button';
import { Badge } from '../UI/badge';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { cn } from '../../lib/utils';
import {
  buildHighRiskSupplierMarkers,
  getMarkerLocationLabel,
  scoreToRiskLevelKey,
} from '../../utils/tenantMapModel';

const riskBadgeClass = (levelKey) => {
  if (levelKey === 'high') return 'bg-danger-500/15 text-danger-500 border-danger-500/30';
  if (levelKey === 'medium') return 'bg-warning-500/15 text-warning-500 border-warning-500/30';
  if (levelKey === 'low') return 'bg-success-500/15 text-success-500 border-success-500/30';
  return 'bg-background-secondary text-text-secondary border-border-light';
};

function formatRiskScore(score) {
  const value = Number(score);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 10 ? value.toFixed(1) : String(Math.round(value * 10) / 10);
}

function TenantMapDetailPanel({
  marker,
  markers = [],
  companyName = '',
  overallRiskScore,
  onClose,
  onMarkerSelect,
  onViewNodeData,
  className,
}) {
  const { t } = useLocale();

  const displayRiskScore = useMemo(() => {
    if (marker?.type === 'own' && overallRiskScore != null && overallRiskScore > 0) {
      return overallRiskScore;
    }
    return marker?.riskScore;
  }, [marker, overallRiskScore]);

  const riskLevelKey = scoreToRiskLevelKey(displayRiskScore);
  const formattedRisk = formatRiskScore(displayRiskScore);

  const highRiskSuppliers = useMemo(
    () => buildHighRiskSupplierMarkers(markers),
    [markers]
  );

  const networkCount = useMemo(
    () => markers.filter((item) => item.type === 'supplier' || item.type === 'own').length,
    [markers]
  );

  const entityTypeLabel = useMemo(() => {
    if (!marker) return '';
    if (marker.type === 'own') return t('tenantMapEntityTypeOwn');
    if (marker.type === 'supplier') return t('tenantMapEntityTypeSupplier');
    return t(`tenantMapMarkerType_${marker.type}`);
  }, [marker, t]);

  const locationLabel = getMarkerLocationLabel(marker);

  if (!marker) return null;

  return (
    <aside
      className={cn(
        'absolute bottom-4 right-4 top-4 z-20 flex w-[min(100%,340px)] flex-col overflow-hidden rounded-xl border border-border-light bg-background-card/95 shadow-xl backdrop-blur-md',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3 border-b border-border-light px-4 py-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-text-primary">{marker.title}</h3>
          <p className="mt-1 text-xs text-text-secondary">
            {t('tenantMapNetworkCount', { count: networkCount })}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={onClose}
          aria-label={t('tenantMapPanelClose')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4">
        {formattedRisk ? (
          <div className="mb-4 flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn('rounded-md px-2.5 py-1 text-sm font-semibold', riskBadgeClass(riskLevelKey))}
            >
              {t('tenantMapRiskBadge', {
                score: formattedRisk,
                level: t(`tenantMapRiskLevel_${riskLevelKey}`),
              })}
            </Badge>
          </div>
        ) : null}

        {locationLabel ? (
          <p className="text-sm text-text-primary">{locationLabel}</p>
        ) : null}

        {entityTypeLabel ? (
          <p className="mt-1 text-xs text-text-secondary">{entityTypeLabel}</p>
        ) : null}

        {marker.operationalStatus ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('tenantMapDetailOperationalStatus')}: {marker.operationalStatus}
          </p>
        ) : null}

        {marker.naturalHazardScore != null && marker.naturalHazardScore !== '' ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('tenantMapDetailNaturalHazard')}: {marker.naturalHazardScore}
          </p>
        ) : null}

        {marker.type === 'supplier' && companyName ? (
          <div className="mt-5 rounded-lg border border-border-light bg-canvas-soft-2 px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-text-secondary">
                {t('tenantMapRelationshipStrength', { company: companyName })}
              </p>
              <span className="text-xs font-semibold text-text-primary">
                {t('tenantMapRelationshipNotAssessed')}
              </span>
            </div>
          </div>
        ) : null}

        <div className="mt-5 flex flex-col gap-2">
          {marker.nodeKey && onViewNodeData ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full justify-between"
              onClick={() => onViewNodeData(marker)}
            >
              {t('tenantMapViewNodeData')}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          ) : null}
          {marker.companyId ? (
            <Button type="button" variant="default" size="sm" className="w-full justify-between" asChild>
              <a href={`/companies/${encodeURIComponent(marker.companyId)}`}>
                {t('tenantMapViewProfile')}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </Button>
          ) : marker.type === 'supplier' && marker.supplierTicker ? (
            <Button type="button" variant="default" size="sm" className="w-full justify-between" asChild>
              <a href={`/companies/${encodeURIComponent(marker.supplierTicker)}`}>
                {t('tenantMapViewProfile')}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </Button>
          ) : null}
        </div>

        {marker.type === 'own' && highRiskSuppliers.length > 0 ? (
          <div className="mt-6">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-text-primary">
                {t('tenantMapHighRiskSuppliers', { count: highRiskSuppliers.length })}
              </p>
              <span className="text-xs text-text-tertiary">{t('tenantMapSortedHighestRisk')}</span>
            </div>
            <ul className="space-y-2">
              {highRiskSuppliers.map((supplier) => {
                const supplierRisk = formatRiskScore(supplier.riskScore);
                const supplierLevel = scoreToRiskLevelKey(supplier.riskScore);
                return (
                  <li key={supplier.id}>
                    <button
                      type="button"
                      onClick={() => onMarkerSelect?.(supplier.id)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border-light bg-background-secondary px-3 py-2.5 text-left transition-colors hover:border-secondary-500/40 hover:bg-canvas-soft-2"
                    >
                      <Shield
                        className="h-4 w-4 shrink-0"
                        style={{ color: colors.secondary[500] }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                        {supplier.title}
                      </span>
                      {supplierRisk ? (
                        <span className="shrink-0 text-xs font-semibold text-danger-500">
                          {t('tenantMapRiskBadgeShort', {
                            score: supplierRisk,
                            level: t(`tenantMapRiskLevel_${supplierLevel}`),
                          })}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export default TenantMapDetailPanel;
